import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { Form, Input, Select } from "antd";
import CustomModal from "../../../components/Common/CustomModal";
import CommonSelect from "components/Common/CommonSelect";

// ⬇️ NEW
import { getRoles } from "../../../services/roleService";
import type { Role } from "../../../types/roleTypes";
import useRoles from "../hooks/useRoles";

interface AddEditUserManagementProps {
  isOpen: boolean;
  onClose: () => void;
  userData: any;
  onSave: (data: any) => void;
}

const AddEditUserManagement = ({
  isOpen,
  onClose,
  userData,
  onSave,
}: AddEditUserManagementProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const { roles , fetchRoles } = useRoles();
  // ⬇️ NEW: roles from backend
  // const [roles, setRoles] = useState<Role[]>([]);
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
    await fetchRoles();
    })();
  }, [isOpen]);

  // allow only these system roles + all instance roles
  const ALLOWED_SYSTEM_SLUGS = useMemo(
    () => new Set(["director", "pedagogy-director"]), // accepts only these two system roles
    []
  );

  // map to CommonSelect options
  const roleOptions = useMemo(
    () =>
      (roles || [])
        .filter(r => (r.isSystem ? ALLOWED_SYSTEM_SLUGS.has(r.slug) : true))
        .map(r => ({
          value: r.id,                 // send roleId
          label: t(r.name),  // i18n key if exists, else name
          slug: r.slug,                // keep slug for legacy 'role'
        })),
    [roles, ALLOWED_SYSTEM_SLUGS, t]
  );

  // default option in edit mode (match by roleId first, then slug)
  const defaultRoleOption = useMemo(() => {
    const byId = roleOptions.find(o => o.value === userData?.roles[0]?.id);
    if (byId) return byId;
    return roleOptions.find(o => o.slug === userData?.roles[0]?.slug);
  }, [roleOptions, userData]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      await onSave(values);       // values contains { ..., roleId: "...", role: "director" }
      onClose();
      form.resetFields();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      form.setFieldsValue({
        ...userData,
        roleId: userData.roleId ?? undefined,
        // role: userData.role ?? "manager", // keep legacy
      });
    } else {
      form.resetFields();
    }
  }, [userData]);

  return (
    <CustomModal
      title={userData?.id ? t("edit_user_management") : t("add_user_management")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={userData?.id ? t("save") : t("add")}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t("first_name")}
          name="firstName"
          rules={[{ required: true, message: t("validation.please_enter_first_name") }]}
        >
          <Input placeholder={t("first_name")} />
        </Form.Item>

        <Form.Item
          label={t("last_name")}
          name="lastName"
          rules={[{ required: true, message: t("validation.please_enter_last_name") }]}
        >
          <Input placeholder={t("last_name")} />
        </Form.Item>

        <Form.Item
          label={t("email")}
          name="email"
          rules={[
            { required: true, message: t("validation.please_enter_email") },
            { type: "email", message: t("validation.please_enter_valid_email") },
          ]}
        >
          <Input placeholder={t("email")} />
        </Form.Item>

 

        {/* keep legacy slug 'role' for backward compatibility */}
        <Form.Item name="role" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item
          label={t("role")}
          name="roleId"
          rules={[
            { required: true, message: t("validation.please_select_role") },
          ]}
        >
          <CommonSelect
            options={roleOptions}
            defaultValue={defaultRoleOption}
            onChange={(option: any) => {
              form.setFieldsValue({
                roleId: option?.value ?? null,
                role: (option?.slug == "director" ? "director" : "manager"),
              });
            }}
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default AddEditUserManagement;
